import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useAdminAuth } from "@/features/admin/hooks/useAdminAuth";
import { LeagueDraftDetail } from "@/features/admin/leagues/components/LeagueDraftDetail";
import { AdminPageHeader } from "@/features/admin/shell/AdminPageHeader";
import { AdminShell } from "@/features/admin/shell/AdminShell";
import { useT } from "@/shared/i18n";
import { EmptyState } from "@/shared/ui/EmptyState";
import { Skeleton } from "@/shared/ui/Skeleton";

export const Route = createFileRoute("/_adminGuarded/admin/leagues/$leagueId")({
  component: AdminLeagueDetailPage,
});

function AdminLeagueDetailPage() {
  const t = useT();
  const { leagueId } = Route.useParams();
  const { state } = useAdminAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const data = useQuery(
    api.admin.getLeagueDraftAdmin,
    token ? { token, leagueId: leagueId as Id<"leagues"> } : "skip",
  );

  const breadcrumbs =
    data === undefined
      ? [{ label: t("admin.leagues.title"), to: "/admin/leagues" }, { label: t("admin.leagues.loading") }]
      : [
          { label: t("admin.leagues.title"), to: "/admin/leagues" },
          { label: data?.league.name ?? t("admin.leagues.notFound") },
        ];

  return (
    <AdminShell breadcrumbs={breadcrumbs}>
      {data === undefined ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-16 w-2/3" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : data === null ? (
        <EmptyState
          title={t("admin.leagues.notFoundTitle")}
          description={t("admin.leagues.notFoundDescription")}
        />
      ) : (
        <>
          <AdminPageHeader
            title={data.league.name}
            subtitle={t("admin.leagues.detailSubtitle", {
              season: data.league.season,
              members: `${data.memberCount}/${data.league.maxManagers}`,
              hitters: data.league.hitterSlots,
              pitchers: data.league.pitcherSlots,
            })}
          />
          <LeagueDraftDetail detail={data} />
        </>
      )}
    </AdminShell>
  );
}
